import { useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Copy, RotateCcw, Send } from "@/components/icons"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"

import { buttonVariants } from "../../../components/ui/button-variants"

export type UserMessageProps = {
  children?: React.ReactNode
  message: string
  /**
   * When set, render a compact "Follow-up" pill instead of the full message
   * bubble — used for Deep Explorer follow-ups where the message body carries
   * an inline plan we don't want to surface back to the user. The agent still
   * receives the full message; only the visual rendering differs.
   */
  followup?: { question: string }
  /** Provenance tag for EXTRA marker bubbles only (issue #2468): a steered
   *  note injected into an already-running turn has no turn of its own, so
   *  it is rendered as a compact pill alongside the real message at that
   *  index — never in place of the primary user bubble. Queued drains and
   *  force redirects are full turns and must use the normal bubble (no
   *  provenance). Mutually exclusive with `followup` in practice — if both
   *  are somehow set, `provenance` wins. */
  provenance?: { kind: "steered"; text: string }
}

export function UserMessage({ message, children, followup, provenance }: UserMessageProps) {
  const { t } = useTranslation()
  const [showCopyIcon, setShowCopyIcon] = useState(false)
  const isMobile = useIsMobile()

  function handleCardClick() {
    if (!showCopyIcon && isMobile) {
      setShowCopyIcon(true)
    }
  }

  async function copyToClipboard() {
    await navigator.clipboard.writeText(message)
    toast.success(t("userchatmessage.copy_text_success"))
  }

  if (provenance) {
    return (
      <Card
        className="group flex flex-col items-end border-0 shadow-none"
        role="presentation"
        onClick={handleCardClick}
      >
        <section className="bg-secondary/60 text-muted-foreground border-border relative flex max-w-[70%] items-start gap-2 rounded-lg rounded-tr-md border px-3 py-2">
          <Send aria-hidden="true" className="text-primary mt-0.5 size-3.5 shrink-0" />
          <CardContent className="p-0">
            <p
              className="text-xs leading-snug break-words"
              data-provenance={provenance.kind}
              data-testid="user-message-text"
              data-text-selectable="true"
            >
              <span className="text-foreground/80 font-medium">
                {t("questioninput.provenance_steered_label", { defaultValue: "Steered" })}:
              </span>{" "}
              <span>{provenance.text}</span>
            </p>
            {children}
          </CardContent>
        </section>
      </Card>
    )
  }

  if (followup) {
    return (
      <Card
        className="group flex flex-col items-end border-0 shadow-none"
        role="presentation"
        onClick={handleCardClick}
      >
        <section className="bg-secondary/60 text-muted-foreground border-border relative flex max-w-[70%] items-start gap-2 rounded-lg rounded-tr-md border px-3 py-2">
          <RotateCcw aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <CardContent className="p-0">
            <p
              className="text-xs leading-snug break-words"
              data-followup="true"
              data-testid="user-message-text"
              data-text-selectable="true"
            >
              <span className="text-foreground/80 font-medium">
                {t("userchatmessage.followup_label", { defaultValue: "Follow-up" })}:
              </span>{" "}
              <span>“{followup.question}”</span>
            </p>
            {children}
          </CardContent>
        </section>
      </Card>
    )
  }

  return (
    <Card
      className="group flex flex-col items-end border-0 shadow-none"
      role="presentation"
      onClick={handleCardClick}
    >
      <section className="bg-secondary text-secondary-foreground border-foreground relative max-w-[70%] rounded-lg rounded-tr-md border p-3">
        <CardContent className="p-0">
          <p
            className="break-words whitespace-pre-wrap"
            data-testid="user-message-text"
            data-text-selectable="true"
          >
            {message}
          </p>
          {children}
        </CardContent>
        <CardFooter
          className={cn(
            // Faded (not display:none) so the copy button stays a keyboard tab stop; it appears
            // on hover, on focus, or after the mobile tap-to-reveal.
            "absolute bottom-2 -left-2 flex -translate-x-full flex-row items-center gap-1 p-0 transition-opacity focus-within:pointer-events-auto focus-within:opacity-100 lg:pointer-events-auto lg:opacity-0 lg:group-hover:opacity-100",
            showCopyIcon ? "opacity-100" : "pointer-events-none opacity-0"
          )}
        >
          <Tooltip>
            <TooltipTrigger
              aria-label={t("userchatmessage.copy_text")}
              type="button"
              className={cn(
                buttonVariants({
                  size: "icon",
                  variant: "icon",
                  className: "size-7",
                })
              )}
              onClick={copyToClipboard}
            >
              <Copy className="size-5" />
            </TooltipTrigger>
            <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
              <p>{t("userchatmessage.copy_text")}</p>
            </TooltipContent>
          </Tooltip>
        </CardFooter>
      </section>
    </Card>
  )
}
